import {
  type ButtonHTMLAttributes,
  type ComponentType,
  type ForwardedRef,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  forwardRef,
} from 'react';
import { twMerge } from 'tailwind-merge';

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>,
    Pick<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?:
    | 'accent'
    | 'outline'
    | 'outline-circular'
    | 'ghost-accent'
    | 'ghost-neutral'
    | 'destructive'
    | 'orange';
  isDiv?: boolean;
  icon: ComponentType | ReactNode;
  onClick?: (event: MouseEvent<HTMLButtonElement | HTMLDivElement>) => void;
  isActive?: boolean;
}

export const IconButton = forwardRef(function IconButton(
  props: IconButtonProps,
  ref: ForwardedRef<HTMLButtonElement | HTMLDivElement>
) {
  const {
    icon: Icon,
    type = 'button',
    disabled = false,
    'aria-label': ariaLabel,
    role = 'button',
    tabIndex = 0,
    isDiv,
    isActive,
    ...buttonProps
  } = props;
  const baseClassName =
    'flex items-center justify-center border focus:outline-none focus:ring-4 cursor-pointer disabled:cursor-default transition ease-in-out duration-200 h-fit relative';
  const colorClassName = {
    accent:
      'bg-branding-accent-600 border-transparent text-white hover:bg-branding-accent-700 focus:bg-branding-accent-600 focus:ring-branding-accent-50 disabled:bg-branding-accent-200 shadow-1',
    outline:
      'bg-white border-branding-gray text-branding-gray-700 hover:bg-gray hover:text-branding-gray-800 focus:bg-white focus:text-branding-gray-700 focus:ring-branding-gray-50 disabled:text-branding-gray-300 disabled:border-branding-gray-200 disabled:bg-white hover:bg-branding-gray shadow-1',
    'outline-circular':
      'bg-white border-branding-gray text-branding-gray-700 hover:bg-gray hover:text-branding-gray-800 focus:bg-white focus:text-branding-gray-700 focus:ring-branding-gray-50 disabled:text-branding-gray-300 disabled:border-branding-gray-200 disabled:bg-white hover:bg-branding-gray shadow-1',
    'ghost-accent':
      'bg-transparent border-transparent text-branding-accent-700 hover:bg-branding-accent-50 focus:ring-0 disabled:text-branding-gray-300 disabled:bg-transparent hover:shadow-1',
    'ghost-neutral':
      'bg-transparent border-transparent text-branding-gray-600 hover:bg-gray focus:ring-0 focus:text-branding-gray-600 disabled:text-branding-gray-300 disabled:bg-transparent hover:bg-branding-gray hover:shadow-1',
    destructive:
      'bg-red-500 border-transparent text-white hover:bg-red-600 focus:bg-red-500 focus:ring-red-50 disabled:bg-red-200 shadow-1',
    orange:
      'bg-branding-accent-orange border-transparent text-white hover:bg-orange-500 focus:ring-orange-100 disabled:bg-orange-200',
  }[props.variant ?? 'ghost-neutral'];
  const activeClassName = {
    accent: 'bg-branding-accent-100',
    outline: 'bg-branding-gray-100',
    'outline-circular': 'bg-branding-gray-100',
    'ghost-accent': 'bg-branding-accent-100',
    'ghost-neutral': 'bg-branding-gray-100',
    destructive: 'bg-red-100',
    orange: 'bg-orange-100',
  }[props.variant ?? 'ghost-neutral'];
  const sizeClassName = {
    xs: 'size-6 rounded-md',
    sm: 'size-8 rounded-lg',
    md: 'size-9 rounded-lg',
    lg: 'size-10 rounded-lg',
    xl: 'size-11 rounded-lg',
  }[props.size ?? 'sm'];
  const iconContainerClassName = {
    xs: 'size-4 [&>svg]:size-4',
    sm: 'size-4 [&>svg]:size-4',
    md: 'size-5 [&>svg]:size-5',
    lg: 'size-5 [&>svg]:size-5',
    xl: 'size-5 [&>svg]:size-5',
  }[props.size ?? 'sm'];
  const shapeClassName = props.variant === 'outline-circular' ? 'rounded-full' : '';
  const classNames = twMerge(
    baseClassName,
    colorClassName,
    sizeClassName,
    shapeClassName,
    isActive ? activeClassName : '',
    props.className ?? ''
  );

  function onClick(event: MouseEvent<HTMLButtonElement | HTMLDivElement>) {
    if (disabled || typeof props.onClick !== 'function') {
      return;
    }
    props.onClick(event);
  }

  const iconContent = typeof Icon === 'function' ? <Icon /> : Icon;

  if (isDiv) {
    return (
      <div
        className={classNames}
        onClick={onClick}
        aria-label={ariaLabel}
        role={role}
        tabIndex={tabIndex}
        aria-disabled={disabled}
        ref={ref as ForwardedRef<HTMLDivElement>}
      >
        <div className={twMerge(iconContainerClassName, 'flex items-center justify-center')}>
          {iconContent}
        </div>
      </div>
    );
  }
  return (
    <button
      type={type}
      disabled={disabled}
      aria-label={ariaLabel}
      role={role}
      tabIndex={tabIndex}
      {...buttonProps}
      className={classNames}
      ref={ref as ForwardedRef<HTMLButtonElement>}
      onClick={onClick}
    >
      <div className={twMerge(iconContainerClassName, 'flex items-center justify-center')}>
        {iconContent}
      </div>
    </button>
  );
});
